import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {InputAdornment, TextField} from "@material-ui/core";
import {Button, Dialog, DialogContent, DialogTitle, Grid, Switch, Typography,} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {Edit} from "@material-ui/icons";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import AddIcon from "@material-ui/icons/Add";
import MaterialTable from "material-table";

import useStyles from "./style";
import AddMedicines from "./AddMedicines";
import UpdateMedicine from "./UpdateMedicine";
import {
  addMedicineApi,
  fetchMedicinesApi,
  searchMedicinesApi,
  toggleMedicineStatusApi,
  updateMedicineApi,
} from "../medicineApi";

const MedicineList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const debounceTimeout = useRef(null);

  // Get medicines list from redux store
  const {medicines, loading, error} = useSelector((state) => ({
    medicines: state.medicine.medicines,
    loading: state.medicine.loading,
    error: state.medicine.error,
  }));

  const [open, setOpen] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!searchQuery) {
      dispatch(fetchMedicinesApi());
    }
  }, [dispatch, searchQuery]);

  const handleAddMedicine = async (newMedicine) => {
    try {
      await dispatch(addMedicineApi(newMedicine));
      setOpen(false);
    } catch (error) {
    }
  };

  const handleSaveEditedMedicine = async (updatedMedicine) => {
    try {
      const {id, ...medicineData} = updatedMedicine;
      await dispatch(updateMedicineApi(id, medicineData));
      setOpenView(false);
    } catch (error) {
      console.error("Failed to update medicine:", error);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (query.length > 0 || (query.length === 0 && searchQuery.length > 0)) {
      debounceTimeout.current = setTimeout(() => {
        dispatch(searchMedicinesApi(query));
      }, 300);
    }
  };

  // const handleDelete = (medicine) => {
  //   if (
  //     window.confirm(
  //       `Are you sure you want to delete ${medicine.medicineName}?`
  //     )
  //   ) {
  //     dispatch(deleteMedicineApi(medicine.id));
  //   }
  // };
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleEdit = (medicine) => {
    setSelectedMedicine(medicine);
    setOpenView(true);
  };

  const handleToggleClick = (medicine) => {
    const newStatus = !medicine.active;
    dispatch(toggleMedicineStatusApi(medicine.id, newStatus)).then(() => {
    });
  };

  const truncateText = (text, limit = 30) =>
    text && text.length > limit ? text.substring(0, limit) + "..." : text;

  // Define columns for MaterialTable (same as before)
  const columns = [
    {
      title: "Medicine Name",
      field: "medicineName",
      validate: (rowData) =>
        rowData.medicineName ? true : "Medicine name is required",
    },
    {
      title: "Dosage",
      field: "dosage",
      render: (rowData) => truncateText(rowData.dosage, 25),
    },
    {
      title: "Frequency",
      field: "frequency",
      render: (rowData) => truncateText(rowData.frequency, 25),
    },
    {
      title: "Instruction",
      field: "instruction",
      render: (rowData) => (
        <span title={rowData.instruction}>
          {truncateText(rowData.instruction, 30)}
        </span>
      ),
    },
    {
      title: "Status",
      field: "active",
      render: (rowData) => (
        <Switch
          checked={rowData.active}
          onClick={(e) => e.stopPropagation()}
          onChange={() => handleToggleClick(rowData)}
          color="primary"
          inputProps={{"aria-label": "toggle medicine status"}}
        />
      ),
    },
  ];

  return (
    <div className={classes.root}>
      {loading}
      {error && <div className={classes.error}>Error: {error}</div>}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className={classes.header}
      >
        <Grid item>
          <Typography variant="h6" className={classes.title}>
            <LocalPharmacyIcon
              style={{verticalAlign: "middle", marginRight: 6}}
            />
            Medicine List
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by medicine name"
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon/>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon/>}
            onClick={() => setOpen(true)}
          >
            Add Medicine
          </Button>
        </Grid>
      </Grid>

      <MaterialTable
        title={"List of Medicines"}
        columns={columns}
        data={medicines || []}
        actions={[
          {
            icon: () => <Edit color="action"/>,
            tooltip: "Edit Medicine",
            onClick: (event, rowData) => handleEdit(rowData),
          },
          // {
          //   icon: () => <Delete color="secondary" />,
          //   tooltip: "Delete Medicine",
          //   onClick: (event, rowData) => handleDelete(rowData),
          // },
        ]}
        options={{
          rowStyle: (rowData) => ({
            opacity: rowData.active ? 1 : 0.5,
          }),
          actionsColumnIndex: -1,
          search: false,
          paging: false,
        }}
        localization={{
          body: {
            emptyDataSourceMessage: "No medicines added.",
          },
        }}
      />

      {openView && selectedMedicine ? (
        <UpdateMedicine
          open={openView}
          onClose={() => setOpenView(false)}
          medicine={selectedMedicine}
          onSave={handleSaveEditedMedicine}
        />
      ) : null}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Medicine</DialogTitle>
        <DialogContent>
          <AddMedicines
            onSubmit={handleAddMedicine}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicineList;
